import React, { useEffect, useState, useRef } from 'react';
import {
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PropTypes from 'prop-types';
import ApexCharts from 'src/components/apexcharts/ApexCharts';
import axiosInstance from 'src/utils/axiosInstance';

const WidgetRadarChart = ({
  containerWidth,
  careersReskillTime,
  onDataLoaded,
  showLabel,
  closeLabel,
  openLabel,
}) => {
  const [radarData, setRadarData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasLoadedRef = useRef(false);
  const thm = useTheme();
  const isXs = useMediaQuery(thm.breakpoints.down('sm'));
  const isMobile = useMediaQuery(thm.breakpoints.down(1025));

  useEffect(() => {
    if (hasLoadedRef.current || !careersReskillTime?.sectorOrSubsector?.length) {
      return;
    }

    const fetchRadarData = async () => {
      try {
        setIsLoading(true);

        const careerIds = [];
        careersReskillTime.sectorOrSubsector.forEach((sector) => {
          if (sector.careers) {
            sector.careers.forEach((career) => {
              if (career.id) {
                careerIds.push(career.id);
              }
            });
          }
        });

        if (careerIds.length === 0) {
          setIsLoading(false);
          return;
        }

        const response = await axiosInstance({
          url: 'frontend/getSkilldarAverage',
          method: 'POST',
          data: { careerIds },
        });

        const chartData = {
          series: response.data.data.series,
          options: {
            fill: {
              opacity: 0.6,
              colors: [],
            },
            tooltip: {
              enabled: true,
              shared: false,
              intersect: true,
              style: {
                fontSize: '12px',
              },
            },
            xaxis: {
              categories: response.data.data.options.xaxis.categories.map((label) =>
                label?.toUpperCase()
              ),
              tickPlacement: 'on',
              labels: {
                show: true,
                style: {
                  fontSize: '12px',
                  fontWeight: 700,
                  colors: Array(10).fill('#000000'),
                },
              },
            },
            grid: {
              borderColor: '#3067DE',
            },
            plotOptions: {
              radar: {
                polygons: {
                  strokeColors: ['#FD4645', '#FFB55B', '#2AA24A', '#933DA9', '#3067DE'],
                },
              },
            },
            legend: {
              show: false,
            },
            yaxis: {
              max: 100,
              tickAmount: 5,
              min: 0,
              show: false,
            },
            chart: {
              animations: {
                enabled: true,
              },
              toolbar: {
                show: false,
              },
            },
            states: {
              hover: {
                filter: {
                  type: 'lighten',
                  value: 0.15,
                },
              },
            },
            markers: {
              size: 4,
              colors: undefined,
              strokeColors: '#fff',
              strokeWidth: 2,
              strokeOpacity: 0.9,
              strokeDashArray: 0,
              fillOpacity: 1,
              shape: 'circle',
              hover: {
                size: 7,
                sizeOffset: 3,
              },
            },
          },
        };
        setRadarData(chartData);
        hasLoadedRef.current = true;

        if (onDataLoaded) {
          onDataLoaded(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching radar data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRadarData();
  }, [careersReskillTime?.sectorOrSubsector?.length, onDataLoaded, openLabel]); // eslint-disable-line react-hooks/exhaustive-deps

  const chartWidth = containerWidth > 1000 ? '100%' : '100%';
  const chartHeight = containerWidth > 1000 ? 470 : 400;

  if (isLoading || !radarData) {
    return <CircularProgress />;
  }

  return (
    <>
      

      <ApexCharts
        className="skilldarChart"
        style={{
          minHeight: isXs ? '400px !important' : '450px !important',
          width: '100% !important',
          margin: isXs ? '0 auto' : 'unset',
        }}
        type="radar"
        height={isXs ? 400 : chartHeight}
        width={chartWidth}
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          maxWidth: isXs ? '100%' : 'unset',
        }}
        chartData={radarData}
        options={{
          ...radarData.options,
          xaxis: {
            ...radarData.options.xaxis,
            labels: {
              ...radarData.options.xaxis.labels,
              style: {
                ...radarData.options.xaxis.labels.style,
                fontSize: isXs ? '7px !important' : '10px',
              },
            },
          },
          legend: {
            ...radarData.options.legend,
            fontSize: isXs ? '9px' : '12px',
            itemMargin: {
              horizontal: isXs ? 4 : 8,
              vertical: isXs ? 1 : 4,
            },
          },
        }}
        series={radarData.series}
      />
    </>
  );
};

WidgetRadarChart.propTypes = {
  containerWidth: PropTypes.number.isRequired,
  careersReskillTime: PropTypes.object,
  onDataLoaded: PropTypes.func,
  showLabel: PropTypes.bool,
  closeLabel: PropTypes.func,
  openLabel: PropTypes.func,
};

export default WidgetRadarChart;
